import { UtmParams, UtmParamKeys } from '@grammarly/website-components'
import { CDN_URL } from '../../constants'
import { getConfig } from '../../config'
import { getLogger } from '../tracking/felog/telemetry'
import { transformToObject } from './error'

export const invalidQueryKeys = ['__nextLocalePatch', 'pid']

export type QueryValue = string | string[] | number | number[] | boolean | boolean[]

export type QueryParams<T = QueryValue> = { [key: string]: T }
export type OptionalQueryParams = QueryParams<QueryValue | undefined>

const isValidQueryValue = (value: QueryValue | undefined): value is QueryValue => value != null

const isValidQueryKey = (key: string) => !invalidQueryKeys.includes(key)

const baseUrl = getConfig().urls.main

const utmParameters: UtmParamKeys[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

export const composeCMSRelativeHref = (path: string, isPreview?: boolean) => {
  return isPreview ? `/preview/page/${path}` : path
}

export const composeUrl = (url: string, params: OptionalQueryParams = {}): string => {
  if (url.startsWith('#')) {
    return url
  }
  try {
    const urlObject = new URL(url, baseUrl)
    return appendQueryParams(urlObject, params)
  } catch (e) {
    getLogger('cms.composeUrl').warn('Could not compose URL and append query parameters', {
      error: transformToObject(e as Error),
      url,
      params,
    })
  }
  return baseUrl
}

export const appendQueryParams = (urlObject: URL, queryParams: OptionalQueryParams) => {
  Object.keys(queryParams).forEach((key) => {
    const value = queryParams[key]
    if (isValidQueryValue(value) && isValidQueryKey(key)) {
      if (Array.isArray(value)) {
        value.forEach((v) => urlObject.searchParams.append(key, v.toString()))
      } else {
        urlObject.searchParams.append(key, value.toString())
      }
    }
  })
  return urlObject.href
}

const isUtmParamKey = (key: string): key is UtmParamKeys => {
  return utmParameters.some((k) => k === key)
}

const extractUtmParamsFromQueryObject = (queryParams: OptionalQueryParams): UtmParams => {
  return Object.entries(queryParams).reduce((acc, [key, value]) => {
    if (typeof value === 'string' && isUtmParamKey(key) && value) {
      return {
        ...acc,
        [key]: value,
      }
    }
    return acc
  }, {})
}

const extractUtmParamsFromURLSearchObject = (queryParams: URLSearchParams): UtmParams => {
  const utmParams: { [key: string]: string } = {}

  queryParams.forEach((value, key) => {
    if (isUtmParamKey(key) && value) {
      utmParams[key] = value as string
    }
  })

  return utmParams
}

export const extractUtmParams = (queryParams: OptionalQueryParams | URLSearchParams): UtmParams => {
  return queryParams instanceof URLSearchParams
    ? extractUtmParamsFromURLSearchObject(queryParams)
    : extractUtmParamsFromQueryObject(queryParams)
}

/**
 * This function is a type-safe way to convert FormData to URLSearchParams.
 * https://github.com/microsoft/TypeScript/issues/30584
 */
export const convertFormDataToUrlSearchParams = (formData: FormData): URLSearchParams => {
  const convertedFormEntries = Array.from(formData, ([key, value]) => [
    key,
    typeof value === 'string' ? value : value.name,
  ])
  return new URLSearchParams(convertedFormEntries)
}

export const getCurrentLocation = () => {
  if (typeof window !== 'undefined') {
    return window.location.href
  }
  return ''
}

/** Returns the Contentful slug without leading or trailing slashes, and converts to unique slugs like `homepage` */
export const getSlugFromStaticPathname = (path: string) => {
  // This allows the bundle to get the correct structure when testing directly on the cdn `https://static-web.grammarly.com/static-fallback/<PAGE>`
  const bucketSubpath = '/static-fallback'
  if (path.startsWith(bucketSubpath)) {
    path = path.slice(bucketSubpath.length)
  }
  if (path.endsWith('/')) {
    path = path.slice(0, path.length - 1)
  }
  if (path.startsWith('/')) {
    path = path.slice(1)
  }
  if (path === '') {
    path = 'homepage'
  }
  return path
}

export const getStructureFilePath = (path: string) => CDN_URL + '/cms-page-data/' + path + '.json.gz'
